import axios from 'axios';
import { PublicClientApplication, Configuration, AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { authConfiguration as configuration } from './AuthConfiguration';

const CONVERSATIONS_API_KEY = "FfpYMDhkPFYcD2z4KB2k5dS9";
const PROJECT_API_KEY = "AeXKaFyQNAbGrkSDAYedEb7M";

let interactionInProgress: boolean = false;
const TOKEN_STORAGE_KEY = "auth_token";
const MSAL_ACCOUNT_KEY = "msal.account.keys";

const MSAL_CONFIG: Configuration = {
    auth: {
        clientId: configuration.appClientId,
        authority: configuration.appAuthority,
    },
    cache: {
        cacheLocation: configuration.cacheLocation,
        storeAuthStateInCookie: false,
    },
};

const LOGIN_REQUEST = {
    scopes: configuration.loginRequestScopes,
};

const TOKEN_REQUEST = {
    scopes: configuration.tokenRequestScopes,
};

const GRAPH_CONFIG = {
    graphUsersEndpoint: configuration.graphUsersEndpoint,
};

const PUBLIC_CLIENT_APPLICATION = new PublicClientApplication(MSAL_CONFIG);

async function initializeMsal(): Promise<void> {
    await PUBLIC_CLIENT_APPLICATION.initialize();
}

initializeMsal();

const handleGetActiveAccount = () : AccountInfo | null => {
   return PUBLIC_CLIENT_APPLICATION.getActiveAccount();
}

const axiosInstance = axios.create({
    headers: {
        "Content-Type": "application/json",
    }
});

/**
 * Sets the interaction progress flag to indicate ongoing or completed user interaction.
 *
 * @param flag - A boolean value to set the interaction progress state.
 */
const setInteractionInProgress = (flag: boolean): void => {
    interactionInProgress = flag;
};

/**
 * Retrieves the keys of MSAL accounts stored in local or session storage.
 *
 * @returns The first MSAL account key as a string, or null if no account keys exist.
 */
const getMsalAccountKeys = (): string | null => {
    let msalAccountKeys =
        localStorage.getItem("msal.account.keys") || sessionStorage.getItem("msal.account.keys");

    if (msalAccountKeys) {
        msalAccountKeys = JSON.parse(msalAccountKeys)[0];
        return msalAccountKeys;
    }

    return null;
};

/**
 * Checks if a user is authenticated by verifying the existence of an MSAL account key.
 *
 * @returns A boolean indicating whether the user is authenticated.
 */
const isAuthenticated = (): boolean => {
    return !!getMsalAccountKeys();
};

/**
 * Handles user sign-in using MSAL.
 * 
 * - Initiates a login popup for user authentication.
 * - Sets the active account in MSAL upon successful login.
 * - Retrieves an access token silently for subsequent API calls.
 * - Optionally stores a callback URL for redirection after login.
 * 
 * @param {string} [callbackUrl] - Optional URL to redirect the user after login.
 * @returns {Promise<void>} Resolves when the sign-in process completes.
 */
const handleSignIn = async (callbackUrl?: string): Promise<void> => {
    if (!interactionInProgress) {
        setInteractionInProgress(true);

        try {
            const loginResponse: AuthenticationResult = await PUBLIC_CLIENT_APPLICATION.loginPopup({
                ...LOGIN_REQUEST,
                redirectUri: configuration.redirectUri,

            });

            if (loginResponse.account) {
                PUBLIC_CLIENT_APPLICATION.setActiveAccount(loginResponse.account);

            }

            const account: AccountInfo | null = PUBLIC_CLIENT_APPLICATION.getActiveAccount();


            if (account) {
                const tokenResponse: AuthenticationResult = await PUBLIC_CLIENT_APPLICATION.acquireTokenSilent({
                    ...LOGIN_REQUEST,
                    account,
                });
                setToken(tokenResponse.accessToken);

            }
        } catch (error) {
            console.error("Error during sign in:", error);
        } finally {
            setInteractionInProgress(false);
            if (callbackUrl != null) {
                localStorage.setItem('redirect_uri', callbackUrl);
            }
            window.location.href = '/callback';
        }
    }
};

/**
 * Stores the provided token in the specified cache location.
 * 
 * - Saves the token in either localStorage or sessionStorage, based on the configuration.
 * - Logs an error if an invalid cache location is specified.
 * 
 * @param {string} token - The access token to be stored.
 */
const setToken = (token: string): void => {
    if (configuration.cacheLocation === "localStorage") {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else if (configuration.cacheLocation === "sessionStorage") {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
        console.error("Invalid storage provided. Use localStorage or sessionStorage.");
    }
};

/**
 * Retrieves the stored token from the cache.
 * 
 * - Checks both localStorage and sessionStorage for the token.
 * - Returns the token if found, otherwise returns null.
 * 
 * @returns {string | null} The retrieved token or null if not found.
 */
const getToken = (): string | null => {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || sessionStorage.getItem(TOKEN_STORAGE_KEY);
};

/**
 * Removes the stored token from the cache.
 * 
 * - Deletes the token from both localStorage and sessionStorage.
 */
const removeToken = (): void => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(MSAL_ACCOUNT_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(MSAL_ACCOUNT_KEY);
};

/**
 * Retrieves the authenticated user's account information.
 * 
 * @returns {AccountInfo | null} The active account if the user is authenticated, otherwise null.
 */
const getIdentity = (): AccountInfo | null => {
    if (isAuthenticated()) {
        return PUBLIC_CLIENT_APPLICATION.getActiveAccount();
    }
    return null;
};

/**
 * Handles user sign-out by clearing the authentication token and redirecting to the logout endpoint.
 * 
 * @async
 * @returns {Promise<void>} Resolves when the sign-out process completes.
 */
const handleSignOut = async (): Promise<void> => {
    if (interactionInProgress || !isAuthenticated()) {
        return;
    }

    setInteractionInProgress(true);
    try {
        const account = PUBLIC_CLIENT_APPLICATION.getActiveAccount();
        if (!account) {
            console.error("No active account found.");
            return;
        }
        try {
            await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}logger/signout`,
                account,
                {
                    headers: {
                        "Content-Type": "application/json",
                    }
                }
            );
        } catch (error) {
            console.error("Error during backend signout:", error);
        }

        
        await PUBLIC_CLIENT_APPLICATION.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
            onRedirectNavigate: () => false,
            account: account,
        });
        removeToken();
        window.location.href = '/';
    } catch (error) {
        console.error("Error during sign out:", error);
    } finally {
        setInteractionInProgress(false); 
    }
};

/**
 * Refreshes the authentication token silently and updates the stored token.
 * 
 * @async
 * @returns {Promise<void>} Resolves when the token is successfully refreshed.
 * Logs an error if the refresh process fails.
 */
const handleRefreshToken = async (): Promise<void> => {
    try {
        const tokenResponse: AuthenticationResult = await PUBLIC_CLIENT_APPLICATION.acquireTokenSilent(
            TOKEN_REQUEST
        );
        setToken(tokenResponse.accessToken);
    } catch (error) {
        console.error("Error refreshing token:", error);
    }
};

/**
 * Acquires an authentication token for the active account.
 * 
 * - Tries to obtain the token silently using `acquireTokenSilent`.
 * - Falls back to a popup method (`acquireTokenPopup`) if silent acquisition fails.
 * - Updates the stored token upon successful acquisition.
 * 
 * @async
 * @returns {Promise<string | null>} The acquired token if successful, or `null` if the acquisition fails.
 */
const acquireToken = async (): Promise<string | null> => {
    try {
        const account = PUBLIC_CLIENT_APPLICATION.getActiveAccount();
        if (!account) {
            console.error("No active account found. User might not be logged in.");
            return null;
        }

        const response = await PUBLIC_CLIENT_APPLICATION.acquireTokenSilent({
            scopes: LOGIN_REQUEST.scopes,
            account,
        });

        setToken(response.accessToken);
        return response.accessToken;
    } catch (error) {
        console.error("Error acquiring token silently:", error);

        try {
            const response = await PUBLIC_CLIENT_APPLICATION.acquireTokenPopup({
                scopes: TOKEN_REQUEST.scopes,
            });
            setToken(response.accessToken);
            return response.accessToken;
        } catch (popupError) {
            console.error("Error acquiring token via popup:", popupError);
            return null;
        }
    }
};

/**
 * Generates the Authorization header with the Bearer token.
 * 
 * - Acquires the token using `acquireToken`.
 * - Formats the token into a Bearer Authorization header.
 * 
 * @async
 * @returns {Promise<string | null>} The Authorization header as a string if the token is available, or `null` otherwise.
 */
const authHeader = async (): Promise<string | null> => {
    const token = await acquireToken();
    return token ? `Bearer ${token}` : null;
};

/**
 * Axios interceptor for attaching the Authorization header to all requests.
 * 
 * - Fetches the latest token using the `authHeader` function.
 * - Adds the `Authorization` header to the request configuration if a valid token is available.
 * - Ensures requests are properly authenticated.
 * 
 * @param {AxiosRequestConfig} config - The outgoing request configuration.
 * @returns {AxiosRequestConfig} - The modified request configuration with the Authorization header.
 * @throws {Promise} - Rejects the request if an error occurs during configuration.
 */
axios.interceptors.request.use(
    async (config) => {
        if (config.skipInterceptor) {
            return config;
        }

        const token = await authHeader();
        if (token) {
            config.headers.Authorization = token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const searchProjects = async (name: string, startDate: string, endDate: string, brand: string) => {
    try {
        const response = await axiosInstance.get('/sdspadmin/api/ProjectsInSDESRO/search', {
            params: { name, startDate, endDate, brand },
            headers: { 'X-Api-Key': PROJECT_API_KEY },
        });
        return response.data;
    } catch (error) {
        console.error('Error searching projects:', error);
        throw error;
    }
};

const getProject = async (projectId: number) => {
    try {
        const response = await axiosInstance.get(`/sdspadmin/api/ProjectsInSDESRO/${projectId}`, {
            headers: { 'X-Api-Key': PROJECT_API_KEY },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting project:', error);
        throw error;
    }
};

const getProjectsInBody = async (projects: any) => {
    try {
        const response = await axiosInstance.post('/sdspadmin/api/ProjectsInSDESRO/InBody', projects, {
            headers: { 'X-Api-Key': PROJECT_API_KEY },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting projects in body:', error);
        throw error;
    }
}

const getRows = async () => {
    try {
        const response = await axiosInstance.get('/luxhub/rows', {
            headers: { 'X-Api-Key': CONVERSATIONS_API_KEY },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting rows:', error);
        throw error;
    }
};

const getRowsDiscussions = async (projects: any) => {
    try {
        const response = await axiosInstance.post('/luxhub/rowdiscussions', projects, {
            headers: { 'X-Api-Key': CONVERSATIONS_API_KEY },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting rows ad discussions:', error);
        throw error;
    }
};

const getRowsDiscussionsPO = async (projects: any) => {
    try {
        const response = await axiosInstance.post('/luxhub/rowdiscussionspo', projects, {
            headers: { 'X-Api-Key': CONVERSATIONS_API_KEY },
        });
        //console.log(response.data);
        return response.data;
    } catch (error) {
        console.error('Error getting rows ad discussions:', error);
        throw error;
    }
};

const getConversations = async () => {
    try {
        const response = await axiosInstance.get('/luxhub/conversations', {
            headers: { 'X-Api-Key': CONVERSATIONS_API_KEY },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting conversations:', error);
        throw error;
    }
};

const getSop = async () => {
    try {
        const response = await axiosInstance.get('/luxhub/memos', {
            headers: { 'X-Api-Key': CONVERSATIONS_API_KEY },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting rows:', error);
        throw error;
    }
};

const getNews = async () => {
    try {
        const response = await axiosInstance.get('/luxhub/news', {
            headers: { 'X-Api-Key': CONVERSATIONS_API_KEY },
        });
        return response.data;
    } catch (error) {
        console.error('Error getting rows:', error);
        throw error;
    }
};

export {
    handleGetActiveAccount,
    handleSignIn,
    searchProjects,
    getProject,
    getRows,
    getConversations,
    getProjectsInBody,
    getRowsDiscussions,
    getRowsDiscussionsPO,
    getSop,
    getNews
};